/**
 * As variáveis que o usuário escolhe no mapa ("Próximos dias") — de que
 * modelo e campos vêm, como viram o número da legenda e as cores. Funções
 * puras (testadas em `previsao.test.ts`).
 *
 * Escalas FIXAS: a mesma cor significa sempre o mesmo valor, em qualquer dia.
 * - Temperatura: 10–40 °C (a friagem leva a 10 °C no sul de RO, AC e MT),
 *   RdYlBu do ColorBrewer invertida — perceptual e legível com deuteranopia.
 * - Chuva: mm/h (média do intervalo), transparente abaixo de 0,1 mm/h; os
 *   passos mudam de 3 h para 6 h depois de +144 h, e "mm em 3 h" ao lado de
 *   "mm em 6 h" enganaria.
 * - Vento: km/h, sequencial (GnBu do ColorBrewer).
 * - Fumaça: MP2,5 em µg/m³ com as faixas e cores da CONAMA 491 (as do
 *   MonitorAr, como no `ar`), "Boa" sem cor para não cobrir o mapa; o AOD de
 *   queimadas com as faixas do gráfico do ECMWF (`composition_bbaod550`).
 * - Cenários (WeatherNext 2, acesso restrito): a fração dos 64 cenários com
 *   chuva no intervalo de 6 h, em faixas de 20%: o YlGnBu do ColorBrewer
 *   INVERTIDO (a chance alta é a mais clara, a que salta no satélite escuro) e
 *   mais transparente embaixo; sem cor abaixo de 10%.
 */
import type { ModeloId, ModeloTempo } from './api';

export type VariavelId = 'chuva' | 'temperatura' | 'vento' | 'fumaca' | 'cenarios';
export type FumacaComo = 'pm25' | 'aod';
/** Os cenários do WeatherNext 2 (acesso restrito): chance de chuva (≥ 1 mm) ou de chuva forte (≥ 10 mm) em 6 h. */
export type CenarioComo = 'chance' | 'forte';

export interface Parada {
  /** Valor a partir do qual vale a cor. */
  v: number;
  cor: string;
  /** Rótulo da faixa (escalas por classe). */
  nome?: string;
  /** Opacidade da faixa (0–255, escalas por classe): a chance baixa mais transparente. Padrão 255. */
  alfa?: number;
}

export interface Paleta {
  /** Contínua (interpola entre paradas) ou por classe (degraus). */
  tipo: 'continua' | 'classes';
  /** Abaixo da primeira parada: transparente. */
  paradas: readonly Parada[];
  unidade: string;
  casas: number;
}

export interface CamadaPrevisao {
  id: VariavelId;
  rotulo: string;
  modelo: ModeloId;
  /** Campos do servidor necessários. */
  campos: readonly string[];
  /** Instante (temperatura, vento, MP2,5) ou intervalo que termina no passo (chuva). */
  tipo: 'instante' | 'intervalo';
  paleta: Paleta;
  /**
   * Valor exibido a partir dos campos (mesma ordem de `campos`), no ponto k;
   * `horas` é a duração do intervalo (só para a chuva).
   */
  valor: (campos: readonly ArrayLike<number>[], k: number, horas: number) => number;
}

const PALETA_TEMPERATURA: Paleta = {
  tipo: 'continua',
  unidade: '°C',
  casas: 0,
  paradas: [
    { v: -50, cor: '#313695' },
    { v: 10, cor: '#313695' },
    { v: 15, cor: '#4575b4' },
    { v: 20, cor: '#74add1' },
    { v: 24, cor: '#abd9e9' },
    { v: 27, cor: '#fee090' },
    { v: 30, cor: '#fdae61' },
    { v: 33, cor: '#f46d43' },
    { v: 36, cor: '#d73027' },
    { v: 40, cor: '#a50026' },
  ],
};

const PALETA_CHUVA: Paleta = {
  tipo: 'continua',
  unidade: 'mm/h',
  casas: 1,
  paradas: [
    { v: 0.1, cor: '#c6dbef' },
    { v: 0.5, cor: '#6baed6' },
    { v: 1, cor: '#3182bd' },
    { v: 2, cor: '#08519c' },
    { v: 5, cor: '#54278f' },
    { v: 10, cor: '#7a0177' },
    { v: 20, cor: '#c51b8a' },
  ],
};

const PALETA_VENTO: Paleta = {
  tipo: 'continua',
  unidade: 'km/h',
  casas: 0,
  paradas: [
    { v: 0, cor: '#f7fcf0' },
    { v: 5, cor: '#ccebc5' },
    { v: 10, cor: '#a8ddb5' },
    { v: 20, cor: '#7bccc4' },
    { v: 30, cor: '#4eb3d3' },
    { v: 40, cor: '#2b8cbe' },
    { v: 60, cor: '#0868ac' },
    { v: 80, cor: '#084081' },
  ],
};

/** CONAMA 491 (MP2,5, 24 h): as faixas e as cores do MonitorAr, como em `ar/faixas.ts`. "Boa" sem cor no mapa. */
const PALETA_PM25: Paleta = {
  tipo: 'classes',
  unidade: 'µg/m³',
  casas: 0,
  paradas: [
    { v: 25, cor: '#FFD156', nome: 'Moderada' },
    { v: 50, cor: '#FFAA71', nome: 'Ruim' },
    { v: 75, cor: '#FF0000', nome: 'Muito ruim' },
    { v: 125, cor: '#9B1BAF', nome: 'Péssima' },
  ],
};

/** As faixas do gráfico de AOD de queimadas do ECMWF (`composition_bbaod550`). */
const PALETA_AOD: Paleta = {
  tipo: 'classes',
  unidade: '',
  casas: 2,
  paradas: [
    { v: 0.1, cor: '#d4d6ec' },
    { v: 0.16, cor: '#aaaedb' },
    { v: 0.23, cor: '#8b92c6' },
    { v: 0.29, cor: '#a1a58f' },
    { v: 0.36, cor: '#c0be5c' },
    { v: 0.42, cor: '#d6d036' },
    { v: 0.49, cor: '#f2e300' },
    { v: 0.55, cor: '#f6c800' },
    { v: 0.61, cor: '#f6a800' },
    { v: 0.68, cor: '#f68b00' },
    { v: 0.74, cor: '#f66c00' },
    { v: 0.81, cor: '#f64d10' },
    { v: 0.87, cor: '#f62f10' },
    { v: 0.94, cor: '#ff0000' },
  ],
};

/**
 * Chance (% dos cenários), por faixas: "8 em cada 10 cenários" lê melhor que um contínuo. Sem nomes: a legenda é
 * a barra numerada (a lista com nomes é a das faixas da CONAMA, com "Boa (sem cor)"). Do azul escuro e
 * transparente (pouca chance) ao amarelo-claro (quase certa), como o satélite escuro pede (julgamento de UI/UX).
 */
const PALETA_CHANCE: Paleta = {
  tipo: 'classes',
  unidade: '%',
  casas: 0,
  paradas: [
    { v: 10, cor: '#225ea8', alfa: 120 },
    { v: 30, cor: '#1d91c0', alfa: 180 },
    { v: 50, cor: '#41b6c4' },
    { v: 70, cor: '#7fcdbb' },
    { v: 90, cor: '#edf8b1' },
  ],
};

const MS_PARA_KMH = 3.6;

/**
 * A camada de uma variável. `tempo` é o modelo do tempo em uso (chuva,
 * temperatura e vento vêm dele; `tempoEmUso` na store); a fumaça é sempre do
 * CAMS e os cenários, do WeatherNext 2.
 */
export function camadaDe(id: VariavelId, fumaca: FumacaComo = 'pm25', cenario: CenarioComo = 'chance', tempo: ModeloTempo = 'ifs'): CamadaPrevisao {
  switch (id) {
    case 'temperatura':
      return { id, rotulo: 'Temperatura', modelo: tempo, campos: ['t2m'], tipo: 'instante', paleta: PALETA_TEMPERATURA, valor: ([t], k) => t[k] };
    case 'chuva':
      return {
        id,
        rotulo: 'Chuva',
        modelo: tempo,
        campos: ['chuva'],
        tipo: 'intervalo',
        paleta: PALETA_CHUVA,
        valor: ([c], k, horas) => (horas > 0 ? c[k] / horas : NaN),
      };
    case 'vento':
      return {
        id,
        rotulo: 'Vento',
        modelo: tempo,
        campos: ['u10', 'v10'],
        tipo: 'instante',
        paleta: PALETA_VENTO,
        valor: ([u, v], k) => Math.hypot(u[k], v[k]) * MS_PARA_KMH,
      };
    case 'fumaca':
      return fumaca === 'aod'
        ? { id, rotulo: 'Fumaça (AOD)', modelo: 'cams', campos: ['bbaod'], tipo: 'instante', paleta: PALETA_AOD, valor: ([a], k) => a[k] }
        : { id, rotulo: 'Fumaça (MP2,5)', modelo: 'cams', campos: ['pm25'], tipo: 'instante', paleta: PALETA_PM25, valor: ([p], k) => p[k] };
    case 'cenarios':
      // Um intervalo de 6 h (o que termina no passo), mas já é uma fração: não divide pelas horas.
      return cenario === 'forte'
        ? { id, rotulo: 'Chance de chuva ≥ 10 mm', modelo: 'wn2', campos: ['forte'], tipo: 'intervalo', paleta: PALETA_CHANCE, valor: ([c], k) => c[k] }
        : { id, rotulo: 'Chance de chuva ≥ 1 mm', modelo: 'wn2', campos: ['chance'], tipo: 'intervalo', paleta: PALETA_CHANCE, valor: ([c], k) => c[k] };
  }
}

/** Todas as variáveis (o link aceita todas); `cenarios` só aparece com o acesso de pesquisa (`variaveisNoTrilho`). */
export const VARIAVEIS: readonly VariavelId[] = ['chuva', 'temperatura', 'vento', 'fumaca', 'cenarios'];

/** As variáveis que os trilhos mostram: os cenários só com o WeatherNext 2 no índice (acesso de pesquisa). */
export function variaveisNoTrilho(comCenarios: boolean): readonly VariavelId[] {
  return comCenarios ? VARIAVEIS : VARIAVEIS.filter((v) => v !== 'cenarios');
}

function hex(c: string): [number, number, number] {
  const n = parseInt(c.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** RGBA empacotado como o `Uint32Array` sobre um `ImageData` (little-endian: 0xAABBGGRR). */
export function empacotar(r: number, g: number, b: number, a = 255): number {
  return ((a << 24) | (b << 16) | (g << 8) | r) >>> 0;
}

/** Cor (empacotada) de um valor; 0 = transparente (sem dado ou abaixo da escala). */
export function corDoValor(p: Paleta, v: number): number {
  const ps = p.paradas;
  if (!Number.isFinite(v) || ps.length === 0 || v < ps[0].v) return 0;
  let k = ps.length - 1;
  while (k > 0 && v < ps[k].v) k--;
  if (p.tipo === 'classes' || k === ps.length - 1) return empacotar(...hex(ps[k].cor), p.tipo === 'classes' ? (ps[k].alfa ?? 255) : 255);
  const a = hex(ps[k].cor);
  const b = hex(ps[k + 1].cor);
  const t = (v - ps[k].v) / (ps[k + 1].v - ps[k].v);
  return empacotar(
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  );
}

/** Pinta uma grade de valores (Uint32 RGBA por pixel). */
export function colorir(valores: ArrayLike<number>, p: Paleta): Uint32Array {
  const out = new Uint32Array(valores.length);
  for (let k = 0; k < valores.length; k++) out[k] = corDoValor(p, valores[k]);
  return out;
}

/** Número para a legenda e a ficha, com a vírgula decimal. */
export function formatarValor(p: Paleta, v: number): string {
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('pt-BR', { minimumFractionDigits: p.casas, maximumFractionDigits: p.casas });
}

/** A cor da paleta em CSS (as barras da ficha usam as mesmas cores do mapa). */
export function corCss(p: Paleta, v: number): string {
  const c = corDoValor(p, v);
  if (c === 0) return 'transparent';
  return `rgb(${c & 255}, ${(c >>> 8) & 255}, ${(c >>> 16) & 255})`;
}
